import { formatDateTime, formatRelative } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';

import { Icon, type IconName } from './Icon';
import { StatusBadge } from './StatusBadge';

export interface TimelineItem {
  id: string;
  kind: string;
  summary: string;
  actorLabel: string;
  createdAt: string;
  detail?: Record<string, unknown> | undefined;
  /**
   * Situacao, grupo, status e lado de quem executou (P-COT-3). Opcionais: a linha do
   * tempo de outro modulo nao os tem.
   */
  situation?: string | undefined;
  situationGroup?: string | undefined;
  situationStatus?: string | undefined;
  actorSide?: string | undefined;
}

const icones: Record<string, IconName> = {
  criada: 'sparkle',
  transicao: 'check',
  observacao: 'file',
  responsavel: 'users',
  valor: 'coins',
};

/**
 * Linha do tempo da operacao.
 *
 * Cada entrada carrega o autor da decisao. Um historico sem autor responde o que
 * aconteceu e nao responde quem decidiu, que costuma ser a pergunta seguinte.
 *
 * A lista e apenas leitura porque o historico e prova, e prova que pode ser
 * editada nao e prova.
 */
export function Timeline({
  items,
  icons,
  emptyText = 'Nenhum evento registrado ainda.',
}: {
  items: TimelineItem[];
  /**
   * Icone por tipo de evento, para a linha do tempo de outro modulo. Os tipos da
   * operacao continuam valendo onde o mapa nao disser outro.
   */
  icons?: Record<string, IconName> | undefined;
  emptyText?: string | undefined;
}) {
  if (items.length === 0) {
    return <p className="px-5 py-6 text-center text-sm text-ink-500">{emptyText}</p>;
  }

  return (
    <ol className="flex flex-col">
      {items.map((item, index) => (
        <li key={item.id} className="relative flex gap-3 px-5 py-3.5">
          {index < items.length - 1 ? (
            <span
              aria-hidden
              className="absolute left-[2.0625rem] top-11 bottom-0 w-px bg-line"
            />
          ) : null}

          <span
            aria-hidden
            className={cn(
              'z-1 flex size-7 shrink-0 items-center justify-center rounded-full',
              'ring-1 ring-inset',
              item.kind === 'transicao'
                ? 'bg-brand-50 text-brand-600 ring-brand-200'
                : 'bg-ink-100 text-ink-500 ring-ink-200',
            )}
          >
            <Icon name={icons?.[item.kind] ?? icones[item.kind] ?? 'file'} size={13} />
          </span>

          <div className="min-w-0 flex-1">
            {item.situationGroup ? (
              <p className="mb-1 flex flex-wrap items-center gap-1.5 text-2xs text-ink-500">
                <StatusBadge kind="grupoSituacao" value={item.situationGroup} />
                {item.situation ? (
                  <span className="font-medium text-ink-700">{item.situation}</span>
                ) : null}
                {item.situationStatus ? (
                  <StatusBadge kind="statusSituacao" value={item.situationStatus} />
                ) : null}
                {item.actorSide ? (
                  <StatusBadge kind="ladoSituacao" value={item.actorSide} />
                ) : null}
              </p>
            ) : null}
            <p className="text-sm font-medium text-ink-800">{item.summary}</p>
            <p className="mt-0.5 text-2xs text-ink-500">
              {item.actorLabel ? `${item.actorLabel} - ` : ''}
              <time dateTime={item.createdAt} title={formatDateTime(item.createdAt)}>
                {formatRelative(item.createdAt)}
              </time>
            </p>
            {motivoDe(item) ? (
              <p className="mt-1.5 rounded-md bg-danger-50 px-2.5 py-1.5 text-2xs text-danger-700">
                {motivoDe(item)}
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Le o motivo do evento, quando houver.
 *
 * O detalhe chega como objeto livre, entao o valor e conferido antes de virar
 * texto. Sem a conferencia, um detalhe de outro formato apareceria na tela como
 * representacao interna de objeto.
 */
function motivoDe(item: TimelineItem): string | null {
  const reason = item.detail?.reason;
  return typeof reason === 'string' && reason !== '' ? reason : null;
}
